import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip } from 'chart.js';
import { Skeleton, Typography } from 'antd';
import { MachineListPanel } from '../MachineListPanel/MachineListPanel';
import { MachineKpiPopover } from '../MachineKpiPopover/MachineKpiPopover';
import { useMachineDashboard } from '../../hooks/MachineDashboardContext';
import { useMachineDashboardWidgetList } from '../../hooks/useMachineDashboardWidgetList';
import { MachineStatsCardBase } from './components/MachineStatsCardBase/MachineStatsCardBase';
import { StyledDoughnutKpiCardLeft, StyledDoughnutKpiCardRight } from './DoughnutKpiCard.styles';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { KpiCardConfig } from 'app/modules/cleaning/widgets/machine-dashboard/types/MachineDashboard.types';
import { MachineDashboardWidgetId } from 'app/modules/cleaning/widgets/machine-dashboard/types/machineDashboardWidgetListState.types';
import { mapWidgetIdToWidgetSection } from 'app/modules/cleaning/widgets/machine-dashboard/utils/machineDashboardWidgetListUtils';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { NoResultsEmpty } from 'lib/components/NoResultsEmpty/NoResultsEmpty';

ChartJS.register(ArcElement, ChartTooltip);

interface DoughnutKpiCardProps {
  config: KpiCardConfig;
  isDashboardLoading?: boolean;
}

export const DoughnutKpiCard = ({ config, isDashboardLoading = false }: DoughnutKpiCardProps): React.JSX.Element => {
  const { t } = useTranslation();
  const widgetId = config.id as MachineDashboardWidgetId;
  const widgetSection = mapWidgetIdToWidgetSection(widgetId);

  const { handleNavigateToDeviceTable } = useMachineDashboard();
  const { machines, thumbnailUrlsByMachineId, isLoading, isLoadingMore, hasMore, loadMore } =
    useMachineDashboardWidgetList(widgetId);

  const alertCount = config.segments[config.alertSegmentIndex]?.value ?? 0;
  const totalCount = config.segments.reduce((sum, s) => sum + s.value, 0);
  const isNoData = !isDashboardLoading && totalCount === 0;

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          data: config.segments.map(s => s.value),
          backgroundColor: config.segments.map(s => s.color),
          hoverBackgroundColor: config.segments.map(s => s.color),
          borderWidth: 0,
        },
      ],
    }),
    [config.segments]
  );

  const chartOptions = useMemo(
    () => ({
      cutout: '75%',
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
    }),
    []
  );

  const renderLeft = (isChartLoading: boolean): React.ReactNode => {
    if (isChartLoading) {
      return (
        <StyledDoughnutKpiCardLeft>
          <div className="doughnut-kpi-card__chart-wrapper doughnut-kpi-card__chart-wrapper--skeleton" />
          <div className="doughnut-kpi-card__legend doughnut-kpi-card__legend--skeleton">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton.Input key={i} active size="small" className="doughnut-kpi-card__legend-skeleton-item" />
            ))}
          </div>
          <div className="doughnut-kpi-card__alert">
            <Skeleton active title={false} paragraph={{ rows: 2 }} />
          </div>
        </StyledDoughnutKpiCardLeft>
      );
    }

    return (
      <StyledDoughnutKpiCardLeft>
        <div className="doughnut-kpi-card__chart-wrapper">
          <Doughnut data={chartData} options={chartOptions} />
          <div className="doughnut-kpi-card__chart-center">
            <p className="doughnut-kpi-card__chart-percent">{config.centerPercent}%</p>
            <p className="doughnut-kpi-card__chart-label">{t(config.centerLabel)}</p>
          </div>
        </div>

        <div className="doughnut-kpi-card__legend">
          {config.segments.map(segment => (
            <span key={segment.label} className="doughnut-kpi-card__legend-item">
              <span className="doughnut-kpi-card__legend-dot" style={{ backgroundColor: segment.color }} />
              <span className="doughnut-kpi-card__legend-text">{t(segment.label)}</span>
            </span>
          ))}
        </div>

        <div className="doughnut-kpi-card__alert">
          <div className="doughnut-kpi-card__alert-count">
            <span className="doughnut-kpi-card__alert-count-number">
              {alertCount}{' '}
              <span className="doughnut-kpi-card__alert-count-label">{t('machineDashboard.machines')}</span>
            </span>
            {alertCount > 0 && (
              <MachineKpiPopover
                title={config.listTitle}
                machines={machines}
                thumbnailUrlsByMachineId={thumbnailUrlsByMachineId}
                hintText={config.hintText}
                widgetSection={widgetSection}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                onLoadMore={loadMore}
              >
                <KccButton
                  type="link"
                  className="doughnut-kpi-card__modal-trigger"
                  aria-label={t(config.listTitle)}
                  icon={<SvgIcon name="externalLink" />}
                />
              </MachineKpiPopover>
            )}
          </div>
          <p className="doughnut-kpi-card__alert-text">{t(config.alertText)}</p>
          <Typography.Link
            className="doughnut-kpi-card__alert-link"
            onClick={(): void => handleNavigateToDeviceTable(widgetSection)}
          >
            {t('machineDashboard.deviceTable')}
          </Typography.Link>
        </div>
      </StyledDoughnutKpiCardLeft>
    );
  };

  const renderNoResults = (): React.ReactNode => <NoResultsEmpty />;

  const renderRight = (): React.ReactNode => (
    <StyledDoughnutKpiCardRight>
      <MachineListPanel
        title={config.listTitle}
        machines={machines}
        cardId={config.id}
        emptyDescription={config.emptyDescription}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={loadMore}
        isEmpty={alertCount === 0}
      />
      {(isLoading || machines.length > 0) && alertCount > 0 && (
        <div className="doughnut-kpi-card__hint">
          {isLoading ? (
            <Skeleton active title={false} paragraph={{ rows: 2 }} />
          ) : (
            <span className="doughnut-kpi-card__hint-body">{t(config.hintText)}</span>
          )}
        </div>
      )}
    </StyledDoughnutKpiCardRight>
  );

  return (
    <MachineStatsCardBase
      config={config}
      isLoading={isDashboardLoading}
      renderLeft={renderLeft}
      renderRight={isNoData ? undefined : renderRight}
      renderEmpty={isNoData ? renderNoResults : undefined}
    />
  );
};
